"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, MapPin, MessageSquare as MessageSquareCheck, PhoneCall, Phone, OctagonAlert as AlertOctagon, ArrowRight, CircleCheck as CheckCircle2, Droplets, Flame, ShieldAlert, Compass, Check } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const whyChooseUsPillars = [{
  id: 1,
  title: "Problem-Focused Service",
  badge: "Root-Cause Diagnostics",
  description: "We diagnose the true source of your plumbing failure before taking action. Every repair is built for long-term reliability rather than temporary surface fixes.",
  icon: Wrench
}, {
  id: 2,
  title: "Local Service",
  badge: "Jonesboro & Clayton Co.",
  description: "Our team operates directly out of Jonesboro, GA. Local presence ensures rapid response times when pressurized water lines fail or sewage lines back up.",
  icon: MapPin
}, {
  id: 3,
  title: "Clear Communication",
  badge: "Upfront & Transparent",
  description: "You receive plain-language explanations and exact upfront pricing before work begins. No hidden overtime fees, no ambiguous billing, and zero surprises.",
  icon: MessageSquareCheck
}, {
  id: 4,
  title: "Easy Contact",
  badge: "Direct 24/7 Dispatch",
  description: "When active flooding threatens your home, you speak directly with local dispatchers—not an unmonitored voicemail or automated ticketing system.",
  icon: PhoneCall
}];
const emergencyTriageItems = [{
  id: 1,
  title: "Burst Pipes",
  detail: "High-pressure line fractures requiring immediate water shutoff and pipe replacement.",
  icon: ShieldAlert
}, {
  id: 2,
  title: "Major Water Leaks",
  detail: "Uncontrolled ceiling, wall, or slab leaks causing structural saturation.",
  icon: Droplets
}, {
  id: 3,
  title: "Sewer Backups",
  detail: "Hazardous mainline blockages pushing wastewater back into home drains.",
  icon: AlertOctagon
}, {
  id: 4,
  title: "Overflowing Toilets",
  detail: "Persistent toilet bowl spills unable to clear with standard plunging.",
  icon: Wrench
}, {
  id: 5,
  title: "Water Heater Leaks",
  detail: "Tank fractures, valve blowouts, or rapid flooding from ruptured storage tanks.",
  icon: Flame
}, {
  id: 6,
  title: "Active Flooding",
  detail: "Rising interior water from broken supply lines or failed shutoff valves.",
  icon: Droplets
}];
const galleryPhotos = [{
  id: 1,
  title: "Dedicated Service Fleet",
  subtitle: "Fully equipped mobile response trucks stationed across Clayton County",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/about_service_truck_fleet.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/about_service_truck_fleet.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/about_service_truck_fleet.webp-wvc-sizes",
  alt: "Equipped Jonesboro emergency plumbing service vehicle loaded with diagnostic tools"
}, {
  id: 2,
  title: "Diagnostic Inspections",
  subtitle: "Precise leak detection and sewer camera assessments on every critical call",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/about_technician_pipe_inspection.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/about_technician_pipe_inspection.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/about_technician_pipe_inspection.png-wvc-sizes",
  alt: "Licensed plumbing specialist performing diagnostic pipe inspection"
}, {
  id: 3,
  title: "Commercial-Grade Gear",
  subtitle: "High-torque hydro jetters, mechanical augers, and pipe fusion tools",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/about_emergency_equipment_station.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/about_emergency_equipment_station.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/about_emergency_equipment_station.png-wvc-sizes",
  alt: "Commercial grade hydrostatic testing and drain snake equipment ready for dispatch"
}];
const coveredAreas = ["Jonesboro", "Morrow", "Forest Park", "Riverdale", "Stockbridge", "Lovejoy", "Ellenwood", "Clayton County"];
export default function Apart() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="what-sets-us-apart" data-nav="light" className="relative bg-background py-14 md:py-20 lg:py-24">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
        {/* MODULE 1: Why Choose Us (4 Pillar Cards) */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-lg bg-secondary px-3.5 py-1 text-xs font-bold tracking-wider uppercase text-secondary-foreground">
            <span className="size-2 rounded-lg bg-accent" />
            <span>Why Choose Us</span>
          </div>

          <h2 className="mt-4 font-sans text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Practical, Dependable Standards for Every Call
          </h2>

          <p className="mt-4 font-sans text-base text-muted-foreground sm:text-lg">
            Plumbing emergencies require immediate competence and verified honesty. We built our entire operation around four dependable principles to protect your home.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {whyChooseUsPillars.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={i} variants={itemVariants} className="flex h-full">
                <Card className="relative flex w-full flex-col justify-between overflow-hidden rounded-xl border border-border bg-card p-0 shadow-xs transition-[border-color,box-shadow,background-color] duration-300 hover:border-primary/40 hover:bg-background hover:shadow-md">
                  {/* Visual Motif: 3px Gold Left Accent Stripe */}
                  <div className="absolute top-0 bottom-0 left-0 w-1 bg-accent" aria-hidden="true" />

                  <div className="p-6">
                    <CardHeader className="p-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex size-12 items-center justify-center rounded-xl bg-secondary text-primary shadow-2xs">
                          <IconComponent className="size-6 text-primary" aria-hidden="true" />
                        </div>
                        <span className="rounded-lg bg-muted px-2.5 py-1 font-sans text-xs font-semibold text-muted-foreground">
                          {item.badge}
                        </span>
                      </div>

                      <CardTitle className="mt-5 font-sans text-lg font-bold text-card-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="mt-3 p-0">
                      <CardDescription className="font-sans text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <div className="border-t border-border/60 bg-muted/30 px-6 py-3">
                    <div className="flex items-center gap-2 font-sans text-xs font-medium text-foreground">
                      <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                      <span>Verified Standard</span>
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* MODULE 2: Emergency Plumbing Module (Navy Banner with H2, 6-Item Triage & CTA) */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={itemVariants} className="mt-16 overflow-hidden rounded-2xl border border-border bg-primary p-6 text-primary-foreground shadow-lg sm:p-8 lg:p-12">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-lg bg-accent/20 px-3 py-1 font-sans text-xs font-bold uppercase tracking-wider text-accent">
              <span className="size-2 rounded-lg bg-accent" />
              24/7 Immediate Triage Dispatch
            </span>
            <h2 className="mt-3 font-sans text-2xl font-bold tracking-tight text-primary-foreground sm:text-3xl lg:text-4xl">
              When You Have a Plumbing Emergency
            </h2>
            <p className="mt-3 font-sans text-sm text-primary-foreground/85 sm:text-base">
              Water damage multiplies with every passing minute. If you are experiencing any of the critical issues below, shut off your main water valve and call our Jonesboro dispatch center immediately.
            </p>
          </div>

          {/* 6-item triage list */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {emergencyTriageItems.map((item, i) => {
            const TriageIcon = item.icon;
            return <div key={item.id} data-index={i} className="flex items-start gap-3 rounded-xl border border-primary-foreground/15 bg-primary-foreground/5 p-4 backdrop-blur-xs transition-[background-color,border-color] duration-200 hover:border-accent/40 hover:bg-primary-foreground/10">
                  <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <TriageIcon className="size-5 text-accent-foreground" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-sans text-base font-bold text-primary-foreground">
                      {item.title}
                    </h3>
                    <p className="mt-1 font-sans text-xs text-primary-foreground/80 sm:text-sm">
                      {item.detail}
                    </p>
                  </div>
                </div>;
          })}
          </div>

          {/* Prominent Emergency CTA Cluster */}
          <div className="mt-10 flex flex-col items-start justify-between gap-6 border-t border-primary-foreground/20 pt-8 sm:flex-row sm:items-center">
            <div>
              <p className="font-sans text-xs font-semibold uppercase tracking-wider text-accent">
                Direct Phone Dispatch
              </p>
              <p className="mt-1 font-sans text-base font-medium text-primary-foreground/90 sm:text-lg">
                Available right now across all Jonesboro neighborhoods.
              </p>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <a href="tel:4708479076" className="w-full sm:w-auto">
                <Button size="lg" className="w-full rounded-lg bg-accent px-8 py-4 font-sans text-base font-bold text-accent-foreground shadow-md transition-all hover:brightness-105 active:scale-98">
                  <Phone className="size-5 shrink-0 fill-current" />
                  <span>Call (470) 847-9076</span>
                </Button>
              </a>

              <Link to="/contact/" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="w-full rounded-lg border-primary-foreground/30 bg-primary-foreground/10 px-6 py-4 font-sans font-semibold text-primary-foreground hover:bg-primary-foreground hover:text-primary active:scale-98">
                  <span>Request Online</span>
                  <ArrowRight className="size-4 shrink-0" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* MODULE 3: Service Areas Module */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={itemVariants} className="mt-16 rounded-2xl border border-border bg-card p-6 shadow-xs sm:p-8 lg:p-10">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-lg bg-secondary px-3 py-1 text-xs font-bold uppercase text-secondary-foreground">
                <Compass className="size-3.5 text-primary" aria-hidden="true" />
                <span>Geographic Coverage</span>
              </div>
              <h2 className="mt-3 font-sans text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Serving Jonesboro &amp; Surrounding Communities
              </h2>
              <p className="mt-3 font-sans text-sm text-muted-foreground sm:text-base">
                We provide rapid-response plumbing coverage throughout Clayton County and nearby South Atlanta municipalities, ensuring dispatched service vehicles reach your doorstep without delay.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {coveredAreas.map((area, i) => <span key={area} data-index={i} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1 font-sans text-xs font-medium text-foreground shadow-2xs">
                    <Check className="size-3.5 text-accent" />
                    <span>{area}</span>
                  </span>)}
              </div>
            </div>

            <div className="w-full shrink-0 lg:w-auto">
              <Link to="/service-areas/" className="inline-block w-full sm:w-auto">
                <Button size="lg" className="w-full rounded-lg bg-primary px-7 py-3.5 font-sans font-bold text-primary-foreground shadow-xs hover:bg-primary/90 active:scale-98">
                  <span>View All Service Areas</span>
                  <ArrowRight className="size-4 shrink-0" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* MODULE 4: Real People. Real Work. (3-Photo Documentary Gallery) */}
        <div className="mt-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-lg bg-secondary px-3.5 py-1 text-xs font-bold tracking-wider uppercase text-secondary-foreground">
              <span className="size-2 rounded-lg bg-accent" />
              <span>Authentic Operations</span>
            </div>
            <h2 className="mt-3 font-sans text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
              Real People. Real Work.
            </h2>
            <p className="mt-3 font-sans text-sm text-muted-foreground sm:text-base">
              Take a look behind our daily emergency operations across Jonesboro—equipped response vehicles, certified field technicians, and professional diagnostic machinery ready around the clock.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {galleryPhotos.map((photo, i) => <motion.div key={photo.id} data-index={i} variants={itemVariants} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                  <img src={photo.src} data-wvc-srcset={photo.srcset} data-wvc-sizes={photo.sizes} alt={photo.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-transparent" />
                </div>
                <div className="flex flex-1 flex-col justify-between p-5 sm:p-6">
                  <div>
                    <h3 className="font-sans text-lg font-bold text-card-foreground">
                      {photo.title}
                    </h3>
                    <p className="mt-2 font-sans text-xs leading-relaxed text-muted-foreground sm:text-sm">
                      {photo.subtitle}
                    </p>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 font-sans text-xs font-semibold text-primary">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>Clayton County Active Unit</span>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}